import type { Unsubscribe } from '@reduxjs/toolkit';
import throttle from 'lodash/throttle';

import { yatPageEditingAnything } from './utils';
import {
    addYatPageBulkItems,
    addLiquidIDItem,
    addYatPageItem,
    removeYatPageItem,
    reorderLiquidID,
    setYatPageBio,
    setYatPageHeading,
    setYatPageLiquidID,
    setYatPagePreviewItem,
    updateYatPageEditStates,
    updateYatPageItem,
    updateYatPageItemsOrder,
    removeYatPageBulkItems,
    type YatPageState,
} from '../yat-page';
import { setNFTManagerPanelClose } from '@/store/actions/nft-manager';
import {
    addPageItem,
    addBulkPageItems,
    deletePageItem,
    publishLiquidIdChanges,
    saveModifiedPageItems,
    updateBioChanges,
    updatePageItem,
    removeBulkPageItems,
} from '@/store/actions/yat-page-item-actions';
import { ContentType } from '@/store/types/yat-page-actions';
import type { AppListenerEffectAPI, AppStartListening } from '@/store/listener-middleware';

import { publish } from '@/components/Sections/Profile/pubSub';
import { glShowFace } from '@/components/Sections/Profile/pubSub/constants';

import { FACE_NAMES } from '@/webgl/components/YatCube';
import { LIQUID_ID_FACES_LENGTH } from '@/pages/Prototype/LiquidIDSection/config';

const dispatchSetNFTManagerPanelClose = ({ dispatch, getState }: AppListenerEffectAPI) => {
    /**
     * As some of the redux stores are always returning new state on an action dispatch from other stores.
     * Best to check the current state before dispatching the action. To reduce react renders.
     */
    const NFTPanelOpen = getState().nftManager.managerPanelOpen;
    if (NFTPanelOpen) {
        dispatch(setNFTManagerPanelClose());
    }
};

/**
 * Listener effect for the `yatPage` slice when updateYatPageEditStates is called we want to close the NftManagerPanel
 * to avoid overlapping panels on mobile.
 */
export const updateYatPageEditStateListenerEffect = (
    action: ReturnType<typeof updateYatPageEditStates>,
    listenerApi: AppListenerEffectAPI,
) => {
    if (yatPageEditingAnything(action.payload)) {
        dispatchSetNFTManagerPanelClose(listenerApi);
    }
};

/**
 * Listener effect for the `yatPage` slice when addLiquidIDItem is called and the total amount of liquidIdItems matches
 * total allowed we want to close the NftManagerPanel. Also update the LiquidID to show the correct face.
 */
export const addLiquidIDItemListenerEffect = (
    action: ReturnType<typeof addLiquidIDItem>,
    listenerApi: AppListenerEffectAPI,
) => {
    const liquidIDItems: YatPageState['liquidIDItems'] = listenerApi.getState().yatPage.liquidIDItems;

    const liquidIDItemsAmount = liquidIDItems.filter((value) => !!value).length;
    if (liquidIDItemsAmount === LIQUID_ID_FACES_LENGTH) {
        listenerApi.dispatch(setNFTManagerPanelClose());
        dispatchSetNFTManagerPanelClose(listenerApi);
    }

    const index = action.payload.face || liquidIDItems.findIndex((a) => a === undefined);
    if (index !== -1) {
        /**
         * Update the LiquidID to show the correct face.
         */
        publish(glShowFace, FACE_NAMES[index]);
    }
};

/**
 * Listener effect for the `yatPage` slice when setYatPagePreviewItem is called we want to close the NftManagerPanel
 * to avoid overlapping panels.
 */
export const setYatPagePreviewItemListenerEffect = (
    action: ReturnType<typeof setYatPagePreviewItem>,
    listenerApi: AppListenerEffectAPI,
) => {
    if (action.payload) {
        dispatchSetNFTManagerPanelClose(listenerApi);
    }
};

/** Use to throttle dispatch function calls */
const throttledDispatch = throttle((dispatch: AppListenerEffectAPI['dispatch'], fn) => dispatch(fn), 300, {
    trailing: false,
});

/**
 * Draft calls
 */
const draftYatPageHeading = (action: ReturnType<typeof setYatPageHeading>, listenerApi: AppListenerEffectAPI) => {
    throttledDispatch(listenerApi.dispatch, updateBioChanges());
};

const draftYatPageBio = (action: ReturnType<typeof setYatPageBio>, listenerApi: AppListenerEffectAPI) => {
    throttledDispatch(listenerApi.dispatch, updateBioChanges());
};

const draftYatPageItemsSave = (
    action: ReturnType<typeof updateYatPageItemsOrder> | ReturnType<typeof addYatPageItem>,
    listenerApi: AppListenerEffectAPI,
) => {
    throttledDispatch(listenerApi.dispatch, saveModifiedPageItems());
};

const draftYatPageItemsDelete = (action: ReturnType<typeof removeYatPageItem>, listenerApi: AppListenerEffectAPI) => {
    throttledDispatch(listenerApi.dispatch, deletePageItem(action.payload));
};

const draftYatPageItemsAdd = (action: ReturnType<typeof addYatPageItem>, listenerApi: AppListenerEffectAPI) => {
    if (action.payload.type !== ContentType.LINK) {
        throttledDispatch(listenerApi.dispatch, addPageItem(action.payload));
    }
};

const draftYatPageItemsUpdate = (action: ReturnType<typeof updateYatPageItem>, listenerApi: AppListenerEffectAPI) => {
    throttledDispatch(listenerApi.dispatch, updatePageItem(action.payload));
};

const draftYatPageItemsAddBulk = (
    action: ReturnType<typeof addYatPageBulkItems>,
    listenerApi: AppListenerEffectAPI,
) => {
    throttledDispatch(listenerApi.dispatch, addBulkPageItems(action.payload));
};

const yatPageItemsRemoveBulk = (
    action: ReturnType<typeof removeYatPageBulkItems>,
    listenerApi: AppListenerEffectAPI,
) => {
    throttledDispatch(listenerApi.dispatch, removeBulkPageItems(action.payload));
};

const draftLiquidIdItemChange = (
    action:
        | ReturnType<typeof addLiquidIDItem>
        | ReturnType<typeof setYatPageLiquidID>
        | ReturnType<typeof reorderLiquidID>,
    listenerApi: AppListenerEffectAPI,
) => {
    throttledDispatch(listenerApi.dispatch, publishLiquidIdChanges({ publish: false }));
};

export function setupYatPageListeners(startListening: AppStartListening): Unsubscribe {
    const subscriptions = [
        startListening({
            actionCreator: updateYatPageEditStates,
            effect: updateYatPageEditStateListenerEffect,
        }),
        startListening({
            actionCreator: setYatPagePreviewItem,
            effect: setYatPagePreviewItemListenerEffect,
        }),
        startListening({
            actionCreator: addLiquidIDItem,
            effect: addLiquidIDItemListenerEffect,
        }),
    ];

    return () => {
        subscriptions.forEach((unsubscribe) => unsubscribe());
    };
}

export function setupDraftYatPageListeners(startListening: AppStartListening): Unsubscribe {
    const subscriptions = [
        // HEADING
        startListening({
            actionCreator: setYatPageHeading,
            effect: draftYatPageHeading,
        }),
        // BIO
        startListening({
            actionCreator: setYatPageBio,
            effect: draftYatPageBio,
        }),
        //===========
        // RE-ORDER ITEMS
        startListening({
            actionCreator: updateYatPageItemsOrder,
            effect: draftYatPageItemsSave,
        }),
        // REMOVE ITEM
        startListening({
            actionCreator: removeYatPageItem,
            effect: draftYatPageItemsDelete,
        }),
        // ADD ITEM
        startListening({
            actionCreator: addYatPageItem,
            effect: draftYatPageItemsAdd,
        }),
        // UPDATE ITEM
        startListening({
            actionCreator: updateYatPageItem,
            effect: draftYatPageItemsUpdate,
        }),
        // ADD BULK ITEMS
        startListening({
            actionCreator: addYatPageBulkItems,
            effect: draftYatPageItemsAddBulk,
        }),
        // REMOVE BULK ITEMS
        startListening({
            actionCreator: removeYatPageBulkItems,
            effect: yatPageItemsRemoveBulk,
        }),
        //============
        // LIQUIDID ADD ITEM
        startListening({
            actionCreator: addLiquidIDItem,
            effect: draftLiquidIdItemChange,
        }),
        // LIQUIDID SET ITEMS (Remove)
        startListening({
            actionCreator: setYatPageLiquidID,
            effect: draftLiquidIdItemChange,
        }),
        // LIQUIDID REORDER ITEMS
        startListening({
            actionCreator: reorderLiquidID,
            effect: draftLiquidIdItemChange,
        }),
    ];

    return () => {
        subscriptions.forEach((unsubscribe) => unsubscribe());
    };
}
